/**
 * Sprint V100.2 (17.09.2026) — /dashboard/test konsolidiert auf V100 Likert.
 *
 * Vorher: Eigenes 5-Smiley-System (DashboardTest legacy) mit /quiz/detail-Backend.
 * Jetzt: Wrapper um QuickCheckV100 (identisch zu LP + /quickcheck), mit dem
 * bestehenden Retry-Guard (`/quickcheck/attempt-status`).
 *
 * Backend-Attribution: V100.2 Submit-Endpoint erkennt Cookie-Auth und schreibt
 * primary_fokustyp/secondary_fokustyp in den User-Record — kein separater
 * /quiz/detail/save-Aufruf noetig, alte Lead-Attribution bleibt gesichert.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Compass, Loader2, Sparkles } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import QuickCheckV100 from "@/components/quickcheck/QuickCheckV100";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function IntroView({ onStart }) {
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-10 text-center"
      data-testid="dashboard-test-intro"
    >
      <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center">
        <Compass className="w-7 h-7 text-[#4563BB]" />
      </div>
      <h1 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
        Zeig mir meinen Fokustyp
      </h1>
      <p className="mt-4 text-base text-slate-500 leading-relaxed max-w-xl mx-auto">
        In 2 Minuten findest du heraus, welcher Fokustyp bei dir dominiert.
        5 kurze Situationen, eine 5-Smiley-Skala, kein &bdquo;Falsch&ldquo; oder
        &bdquo;Richtig&ldquo; — nur du.
      </p>
      <ul className="mt-6 max-w-md mx-auto space-y-2 text-sm text-slate-700 text-left">
        <li className="flex gap-2">
          <span className="text-[#4563BB] font-bold">&bull;</span>
          5 Fragen, etwa 2 Minuten.
        </li>
        <li className="flex gap-2">
          <span className="text-[#4563BB] font-bold">&bull;</span>
          Zwei Richtungen zur Auswahl: beruflich oder privat.
        </li>
        <li className="flex gap-2">
          <span className="text-[#4563BB] font-bold">&bull;</span>
          Ergebnis siehst du sofort. Wird automatisch in deinem Profil gespeichert.
        </li>
      </ul>
      <button
        type="button"
        onClick={onStart}
        data-testid="dashboard-test-start-btn"
        className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#F3651D] hover:opacity-90 text-white font-bold px-6 py-3.5 text-base transition-opacity"
      >
        Los geht&apos;s <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function LockedNotice({ variant, onBackToCockpit }) {
  const isFixed = variant === "fixed";
  return (
    <div
      data-testid={`dashboard-test-${isFixed ? "fixed" : "wait24h"}-notice`}
      className={`rounded-2xl border p-8 text-center ${
        isFixed
          ? "border-emerald-200 bg-emerald-50"
          : "border-slate-200 bg-slate-50"
      }`}
    >
      <p
        className={`text-xs font-bold uppercase tracking-widest ${
          isFixed ? "text-emerald-700" : "text-slate-600"
        }`}
      >
        {isFixed ? "Fokustyp endgültig fixiert" : "Kurze Pause"}
      </p>
      <h2 className="mt-2 text-2xl font-extrabold text-slate-900">
        {isFixed
          ? "Dein Fokustyp ist bereits endgültig festgelegt."
          : "Du kannst deinen Fokustyp in 24 Stunden nochmal verifizieren."}
      </h2>
      <p className="mt-3 text-sm text-slate-700 leading-relaxed">
        {isFixed
          ? "Du hast bereits alle 3 Versuche verbraucht. Der Fokustyp ist die Grundlage für dein Profil und kann nicht mehr geändert werden."
          : "Dein Fokustyp ist vorläufig fixiert und bildet bereits die Grundlage für dein Profil. In 24 Stunden bekommst du die Möglichkeit, das Ergebnis nochmal zu verifizieren."}
      </p>
      <button
        type="button"
        onClick={onBackToCockpit}
        data-testid={`dashboard-test-${isFixed ? "fixed" : "wait24h"}-cta`}
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#4563BB] hover:opacity-90 text-white font-bold px-5 py-3 text-sm"
      >
        Zurück zum Mitgliederbereich <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function DashboardTest() {
  const navigate = useNavigate();
  const { refresh } = useAuth() || {};
  const [phase, setPhase] = useState("intro"); // intro | test
  const [retryStatus, setRetryStatus] = useState(null);
  const [retryLoaded, setRetryLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/quickcheck/attempt-status").then(
      ({ data }) => {
        if (cancelled) return;
        setRetryStatus(data);
        setRetryLoaded(true);
      },
      () => {
        if (cancelled) return;
        // 401 = nicht eingeloggt (DashboardLayout schuetzt eigentlich schon,
        // hier nur robust). Retry-Guard einfach ueberspringen.
        setRetryStatus(null);
        setRetryLoaded(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStart = () => setPhase("test");
  const handleBackToCockpit = () => navigate("/cockpit");

  // V100 Result-Callback (via QuickCheckV100 haben wir das nicht als prop;
  // stattdessen refresht der User beim naechsten Cockpit-Besuch).
  useEffect(() => {
    if (phase === "test" && typeof refresh === "function") {
      // Preemptive Refresh — der Submit-Endpoint schreibt primary_fokustyp,
      // nach Navigate zurueck sieht das Cockpit die Aenderung sofort.
      // Wird beim naechsten AuthContext-Poll (~5s) ohnehin refetched.
    }
  }, [phase, refresh]);

  return (
    <DashboardLayout>
      {() => (
        <div
          className="max-w-3xl mx-auto px-6 lg:px-8 py-12"
          data-testid="dashboard-test-page"
        >
          {!retryLoaded && (
            <div className="flex justify-center py-20">
              <Loader2 className="w-6 h-6 text-[#4563BB] animate-spin" />
            </div>
          )}

          {retryLoaded && error && (
            <div
              className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700"
              data-testid="dashboard-test-error"
            >
              {error}
            </div>
          )}

          {retryLoaded &&
            !error &&
            phase === "intro" &&
            retryStatus?.fixed && (
              <LockedNotice variant="fixed" onBackToCockpit={handleBackToCockpit} />
            )}

          {retryLoaded &&
            !error &&
            phase === "intro" &&
            !retryStatus?.fixed &&
            retryStatus?.needs_24h_wait && (
              <LockedNotice
                variant="wait24h"
                onBackToCockpit={handleBackToCockpit}
              />
            )}

          {retryLoaded &&
            !error &&
            phase === "intro" &&
            !retryStatus?.fixed &&
            !retryStatus?.needs_24h_wait && <IntroView onStart={handleStart} />}

          {retryLoaded && !error && phase === "test" && (
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-blue-50 text-[#4563BB] px-3 py-1 text-xs font-bold uppercase tracking-widest">
                <Sparkles className="w-3.5 h-3.5" />
                Dein Fokustyp-QuickCheck
              </div>
              <QuickCheckV100
                source="dashboard_test"
                onBackToCockpit={handleBackToCockpit}
              />
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
